import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from 'oidc-react';
import { Download, LoaderCircle, RotateCcw } from 'lucide-react';
import Sidebar from '../../components/sidebar/sidebar';
import Header from '../../components/header';
import Card from '../../components/Card';
import Pagination from '../../components/Pagination/Pagination';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import { downloadCSV } from '../../common/Utils/downloadHelpers';
import { AuditEvent } from '../../components/services/auditService';
// Not './ActivityLogFilters': on case-insensitive file systems that resolves to activityLogFilters.ts first.
import ActivityLogFilterForm from './ActivityLogFilterForm';
import ActivityLogTable from './ActivityLogTable';
import EventDetailDrawer from './EventDetailDrawer';
import { ActivityLogFilterValues, filtersToQuery, filtersToSearchParams, parseFilters } from './activityLogFilters';
import { activityLogCsvFileName, buildActivityLogCsv } from './activityLogExport';
import { formatAuditTime } from './auditFormat';
import { useActivityLog } from './useActivityLog';
import { useActivityLogTenants } from './useActivityLogTenants';
import { useAuditActions } from './useAuditActions';
import { useUserDirectory } from './useUserDirectory';
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from './buttonStyles';

const StateMessage = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-gray-400">{children}</div>
);

const ActivityLog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  const { levelOf, role } = usePermissions();
  // Global and cross-tenant views are Globaladmin-only on the AuditAPI ('all' = every tenant).
  const canUseGlobalScopes = levelOf('activityLog') === 'all';
  // Globaladmin and reseller pick the tenant in the Scope dropdown: their own tenant or an associated one. The
  // AuditAPI checks access to whichever tenant is sent (reseller -> mapped tenants only).
  const canChooseTenant = canUseGlobalScopes || role === 'reseller';
  const roleLabel = canUseGlobalScopes ? 'Global admin' : 'Reseller';
  const tenants = useActivityLogTenants(canChooseTenant);
  const actions = useAuditActions();

  // Filters live in the URL so a filtered view can be bookmarked or shared; keyed on the string so the
  // derived objects (and the data hook's inputs) only change when the query string does.
  const searchKey = searchParams.toString();
  const filters = useMemo(
    () => parseFilters(new URLSearchParams(searchKey), canUseGlobalScopes, canChooseTenant),
    [searchKey, canUseGlobalScopes, canChooseTenant],
  );
  const query = useMemo(() => filtersToQuery(filters), [filters]);
  // The tenant picked in Scope, else the header's selected tenant.
  const tenantId = filters.tenantId || selectedTenantId;
  // User filter list: AuthService's user directory for the signed-in user's own tenant, whatever the Scope. Globaladmin
  // -> global admins, resellers with their tenants, other tenants; reseller -> global admins, its users, its tenants;
  // tenant admin -> global admins and its tenant's users. Nothing is fetched until the User dropdown is opened.
  const ownTenantId = auth?.userData?.profile?.tenant_id ? String(auth.userData.profile.tenant_id) : null;
  const users = useUserDirectory(ownTenantId);

  const { items, pagination, pageNumber, range, status, error, pageLoading, pageError, goToPage, reload, fetchForExport } = useActivityLog(
    filters.scope,
    tenantId,
    query,
  );
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);
  const { addToast } = useToast();
  const [exporting, setExporting] = useState(false);
  const showTenantColumn = filters.scope === 'all';

  const applyFilters = (next: ActivityLogFilterValues) => setSearchParams(filtersToSearchParams(next));

  // The newest events for the current scope and filters (up to the API's limit) in one request, converted to CSV here.
  const downloadActivity = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const response = await fetchForExport();
      if (!response?.isSuccess || !response.data) {
        addToast(response?.message || 'Could not download the activity log. Please try again.', 'error', 5000);
        return;
      }
      const events = response.data.items ?? [];
      if (events.length === 0) {
        addToast('No activity to download for these filters and dates.', 'info', 4000);
        return;
      }
      const { columns, rows } = buildActivityLogCsv(events, {
        actionNames: actions.nameByCode,
        tenantNames: tenants.namesById,
        showTenantColumn,
        fullDetail: canUseGlobalScopes,
      });
      downloadCSV(rows, activityLogCsvFileName(filters.scope), columns);
      const total = response.pagination?.totalRecords ?? events.length;
      if (total > events.length) {
        addToast(
          `Downloaded the newest ${events.length.toLocaleString()} of ${total.toLocaleString()} events. Narrow the dates or filters to download the rest.`,
          'warning',
          8000,
        );
      }
    } catch {
      // The shared apiService interceptor has already shown the error toast.
    } finally {
      setExporting(false);
    }
  };

  const totalRecords = pagination?.totalRecords ?? 0;

  const renderBody = () => {
    if (status === 'noTenant') return <StateMessage>Select a tenant to see its activity.</StateMessage>;

    if (status === 'loading') {
      return (
        <StateMessage>
          <LoaderCircle className="h-6 w-6 animate-spin" />
          <span>Loading activity…</span>
        </StateMessage>
      );
    }

    if (status === 'error') {
      return (
        <StateMessage>
          <span role="alert" className="text-red-400">
            {error}
          </span>
          <button type="button" onClick={reload} className={PRIMARY_BUTTON}>
            <RotateCcw size={16} />
            <span>Try again</span>
          </button>
        </StateMessage>
      );
    }

    if (items.length === 0) return <StateMessage>No activity found for these filters and dates.</StateMessage>;

    return (
      <>
        <ActivityLogTable items={items} showTenantColumn={showTenantColumn} tenantNames={tenants.namesById}
          actionNames={actions.nameByCode}
          onSelect={canUseGlobalScopes ? setSelectedEvent : undefined} fullTarget={canUseGlobalScopes} />
        <div className="mt-2 flex flex-col items-center gap-2">
          {pageError && (
            <span role="alert" className="text-sm text-red-400">
              {pageError}
            </span>
          )}
          <Pagination currentPage={pageNumber} totalPages={pagination?.totalPages ?? 0} loading={pageLoading} onPageChange={goToPage} />
        </div>
      </>
    );
  };

  return (
    <div className="flex">
      <Sidebar></Sidebar>
      <div className="flex-1" style={{ backgroundColor: 'rgb(7, 7, 7)' }}>
        <Header title="Activity Log" dropdown={2} />
        <Card className="flex-1" bgColor="rgb(7, 7, 7)" border={true} borderColor="#474646" borderSide="left">
          <div className="p-4 text-white">
            <ActivityLogFilterForm
              value={filters}
              canUseGlobalScopes={canUseGlobalScopes}
              canChooseTenant={canChooseTenant}
              roleLabel={roleLabel}
              tenants={tenants}
              actionOptions={actions.options}
              users={users}
              fallbackTenantId={selectedTenantId}
              onApply={applyFilters}
            />
            <div className="mt-4 mb-2 flex items-center justify-between gap-3 text-xs text-gray-400">
              <span>
                {range ? `Showing ${formatAuditTime(range.fromUtc)} – ${formatAuditTime(range.toUtc)}, newest first` : ''}
                {pagination ? ` · ${totalRecords.toLocaleString()} ${totalRecords === 1 ? 'event' : 'events'}` : ''}
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={downloadActivity}
                  disabled={status !== 'success' || items.length === 0 || exporting}
                  title="Download the newest events for these filters as CSV"
                  className={SECONDARY_BUTTON}
                >
                  {exporting ? <LoaderCircle size={16} className="animate-spin" /> : <Download size={16} />}
                  <span>{exporting ? 'Downloading…' : 'Download CSV'}</span>
                </button>
                <button type="button" onClick={reload} aria-label="Refresh" title="Refresh" className="hover:text-white">
                  <RotateCcw className="h-4 w-4" />
                </button>
              </div>
            </div>
            {renderBody()}
          </div>
        </Card>
      </div>
      {/* Event detail (field-level changes) is Globaladmin only; the AuditAPI sends other roles only the list summary. */}
      {canUseGlobalScopes && <EventDetailDrawer event={selectedEvent} actionNames={actions.nameByCode} onClose={() => setSelectedEvent(null)} />}
    </div>
  );
};

export default ActivityLog;
